import { Resources } from 'constants/resources';

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { RequestsBankOpsFormValues } from 'pages/Finance/RequestsBankOps/components/RequestsBankOpsForm';
import { RequestsBankOpsFormSkeleton } from 'pages/Finance/RequestsBankOps/components/RequestsBankOpsFormSkeleton';
import { useRequestsBankOpsEditable } from 'pages/Finance/RequestsBankOps/hooks/useRequestsBankOpsEditable';
import { useUpdateBankOperation } from 'pages/Finance/RequestsBankOps/hooks/useUpdateBankOperation';
import { useUpdateRelatedBankOperation } from 'pages/Finance/RequestsBankOps/hooks/useUpdateRelatedBankOperation';
import { BANK_OPERATION_SCHEMA } from 'pages/Finance/RequestsBankOps/schemas/schema';
import { NotFound } from 'pages/NotFound';
import { SimpleForm, useEditController, useGetOne } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { Box } from 'shared/mui/Box/Box';
import { CancelButton, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { EditPage } from 'shared/react-admin/Pages/EditPage/EditPage';
import { BankOperation } from 'types/embedded/bankOperation';
import { formatInputToTimestamp } from 'utils/date';

import { RequestsBankOpsEditForm } from './RequestsBankOpsEditForm';

export const RequestsBankOpsEdit: React.FC = () => {
  const { record, isLoading } = useEditController();
  const { isEdit } = useRequestsBankOpsEditable();
  const [errors, setErrors] = useState();

  const { updateBankOperation } = useUpdateBankOperation(setErrors);
  const { updateRelatedBankOperation } = useUpdateRelatedBankOperation(setErrors);
  const [isRelated, setIsRelated] = useState(!!record?.related);

  const handleSubmit = useCallback(
    (data: FieldValues) => {
      return isRelated
        ? updateRelatedBankOperation(data as BankOperation)
        : updateBankOperation(data as BankOperation);
    },
    [isRelated],
  );

  const { data: relatedRecord } = useGetOne<BankOperation>(
    Resources.RequestBankOperations,
    { id: record?.related?.['@id'] },
    { enabled: !!record && !!record.related },
  );

  const defaultValues: RequestsBankOpsFormValues = useMemo(() => {
    const values: RequestsBankOpsFormValues = {
      id: record?.id,
      amountPenny: record?.amountPenny,
      currency: record?.currency?.['@id'],
      description: record?.description,
      direction: record?.direction?.['@id'],
      merchant: record?.merchant?.['@id'],
      merchantAccount: record?.merchantAccount?.['@id'],
      provider: record?.provider?.['@id'],
      referral: record?.referral?.['@id'],
      type: record?.type?.['@id'],
      related: undefined,
      dateOfOperation: formatInputToTimestamp(record?.dateOfOperation) || undefined,
    };

    if (relatedRecord) {
      values.related = {
        id: relatedRecord?.id,
        amountPenny: relatedRecord?.amountPenny,
        currency: relatedRecord?.currency?.['@id'],
        direction: relatedRecord?.direction?.['@id'],
        merchant: relatedRecord?.merchant?.['@id'],
        merchantAccount: relatedRecord?.merchantAccount?.['@id'],
        provider: relatedRecord?.provider?.['@id'],
        referral: relatedRecord?.referral?.['@id'],
      };
    }

    return values;
  }, [record, relatedRecord]);

  useEffect(() => {
    if (record?.related) {
      setIsRelated(true);
    }
  }, [record]);

  if (!isEdit(record as BankOperation) && !isLoading) {
    return <NotFound />;
  }

  return (
    <EditPage
      listName="Заявки на банковскую операцию"
      mutationMode="pessimistic"
      title="Редактирование заявки на банковскую операцию"
    >
      {!record ? (
        <RequestsBankOpsFormSkeleton />
      ) : (
        <SimpleForm
          noValidate
          onSubmit={handleSubmit}
          record={defaultValues}
          resolver={yupResolver<FieldValues>(BANK_OPERATION_SCHEMA)}
          shouldUseNativeValidation={false}
          sx={{ '&.MuiCardContent-root': { padding: 0 } }}
          toolbar={false}
        >
          <Box sx={{ paddingY: '24px', width: '100%' }}>
            <RequestsBankOpsEditForm
              errors={errors}
              isRelated={isRelated}
              setIsRelated={setIsRelated}
            />
          </Box>
          <CardActionsButtons
            rightActionsSlot={[
              <CancelButton key="cancel-button" linkType="back" variant="outlined" />,
              <SaveButton key="save-button" label="Сохранить" />,
            ]}
            sx={{ marginTop: 'auto', padding: 0 }}
          />
        </SimpleForm>
      )}
    </EditPage>
  );
};
